import { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  PinInput,
  PinInputField,
  Text,
  VStack,
} from "@chakra-ui/react";
import { FaShieldAlt } from "react-icons/fa";

const DELIVERY_OTP_LENGTH = 4;

export interface DeliveryOtpModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerLabel?: string;
  orderNo?: string;
  isSubmitting?: boolean;
  errorMessage?: string;
  onVerify: (otp: string) => void | Promise<void>;
}

/**
 * Chakra-only OTP modal for Orca (via LMD). Uses PinInput + theme tokens so it
 * stays compatible with Orca's ChakraProvider. No custom CSS variables / media.
 */
export default function DeliveryOtpModal({
  isOpen,
  onClose,
  customerLabel,
  orderNo,
  isSubmitting = false,
  errorMessage,
  onVerify,
}: DeliveryOtpModalProps) {
  const [otp, setOtp] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setOtp("");
    }
  }, [isOpen]);

  const isOtpComplete = otp.length === DELIVERY_OTP_LENGTH;
  const subtitle = customerLabel || orderNo || "";

  const handleVerify = () => {
    if (!isOtpComplete || isSubmitting) {
      return;
    }
    void onVerify(otp);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size="sm"
      returnFocusOnClose={false}
      closeOnOverlayClick={!isSubmitting}
      closeOnEsc={!isSubmitting}
    >
      <ModalOverlay />
      <ModalContent borderRadius="xl" mx={4}>
        <ModalBody px={6} pt={8} pb={6}>
          <VStack spacing={5} align="stretch">
            <VStack spacing={3} textAlign="center">
              <Box
                w={12}
                h={12}
                borderRadius="full"
                bg="blue.500"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon as={FaShieldAlt} boxSize={6} color="white" />
              </Box>

              <Text fontSize="xl" fontWeight="bold" color="gray.800">
                Enter delivery OTP
              </Text>

              {subtitle ? (
                <Text fontSize="md" fontWeight="semibold" color="gray.700">
                  {subtitle}
                </Text>
              ) : null}

              {orderNo && customerLabel ? (
                <Text fontSize="md" fontWeight="semibold" color="gray.700" mt={-2}>
                  Order {orderNo}
                </Text>
              ) : null}

              <Text fontSize="sm" color="gray.500">
                Enter the 4-digit delivery OTP from the customer to complete
                delivery.
              </Text>
            </VStack>

            <FormControl isInvalid={Boolean(errorMessage)}>
              <HStack justify="center" spacing={3}>
                <PinInput
                  otp
                  size="lg"
                  value={otp}
                  onChange={setOtp}
                  isDisabled={isSubmitting}
                  focusBorderColor="blue.400"
                >
                  <PinInputField borderRadius="md" />
                  <PinInputField borderRadius="md" />
                  <PinInputField borderRadius="md" />
                  <PinInputField borderRadius="md" />
                </PinInput>
              </HStack>
              {errorMessage ? (
                <FormErrorMessage justifyContent="center" textAlign="center">
                  {errorMessage}
                </FormErrorMessage>
              ) : null}
            </FormControl>

            {/* Same button pattern as Orca/LMD GaraazAlertDialogue */}
            <HStack spacing={3} justify="flex-end" pt={1}>
              <Button
                flex={1}
                bg="gray.100"
                color="gray.800"
                _hover={{ bg: "gray.200" }}
                onClick={onClose}
                isDisabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                flex={1}
                colorScheme="blue"
                bg="blue.500"
                _hover={{ bg: "blue.600" }}
                onClick={handleVerify}
                isDisabled={!isOtpComplete || isSubmitting}
                isLoading={isSubmitting}
              >
                Verify & Deliver
              </Button>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
